import moment from 'moment'

import { BREAKPOINT } from './api'
import './calendar.scss'

import DataAdapter from '@/components/calendar/integration/DataAdapter'
import {
  DefaultDataInitializer,
  ListingPageDataInitializer,
  PopupDataInitializer,
} from '@/components/calendar/integration/DataInitializer'
import DateDisplay from '@/components/calendar/integration/DateDisplay'
import Calendar from '@/components/Calendar.vue'
import MobileCalendar from '@/components/MobileCalendar.vue'
import * as events from '@/events'
import QueryString from '@/utils/QueryString'

function removeQueryStringParameter (name) {
  new QueryString().delete(name)
}

function setQueryStringParameter (name, value) {
  new QueryString().set(name, value)
}

function triggerChangeEvent (element) {
  element.dispatchEvent(new CustomEvent('change'))
}

export default {
  init (form) {
    let currentCalendar = null

    const dateField = form.querySelector('.custom-select-date')
    if (!dateField) {
      log.debug('calendar.js / init - date field not found - skipping')
      return
    }

    if (dateField.dataset.initialized === 'true') {
      log.debug('calendar.js / init - form already initialized - skipping')
      return
    }
    dateField.dataset.initialized = 'true'

    if (form.classList.contains('form-booking')) {
      log.debug('calendar.js / init - form-booking does not contain popup calendar - skipping')
      return
    }

    const main = this.createMainElementFor(form, dateField)
    const title = main.parentNode.querySelector('.section-title')
    const rangeField = form.querySelector('.custom-select-duration .section-title')

    if (form.classList.contains('offer-search-advanced')) {
      main.data.initializer = new ListingPageDataInitializer(main)
    } else if (form.classList.contains('form-filters-popup')) {
      main.data.initializer = new PopupDataInitializer(main)
    } else {
      main.data.initializer = new DefaultDataInitializer(main)
    }

    // Set initial value for the title of the field
    if (main.data.mode === 'start')
      this.displayTitle(title, 'Wylot od - do')
    else
      this.displayTitle(title, 'Miesiąc')

    // Since there is always data in the field so it needs to be displayed always
    main.data.title.showLabel()

    const closeCalendar = () => {
      currentCalendar.close()
      currentCalendar = null
      events.closeCalendars.unsubscribe(closeCalendarWhenClickedOutside)
      document.body.removeEventListener('click', closeCalendarWhenClickedOutside)
      const people = form.querySelector('.custom-select-people')
      if (people) {
        people.removeEventListener('click', closeCalendarWhenClickedOutside)
      }
    }

    const closeCalendarWhenClickedOutside = e => {
      if (currentCalendar && !e.target.closest('.custom-select-date')) {
        closeCalendar()
      }
    }

    const openCalendar = mode => {
      currentCalendar = this.open(mode, main, title, rangeField)
      currentCalendar.instance.onClose = () => {
        currentCalendar = null
      }
      if (window.innerWidth > BREAKPOINT.TABLET) {
        events.closeCalendars.subscribe(closeCalendarWhenClickedOutside)
        document.body.addEventListener('click', closeCalendarWhenClickedOutside)
        const people = form.querySelector('.custom-select-people')
        if (people) {
          people.addEventListener('click', closeCalendarWhenClickedOutside, { capture: true })
        }
      }
    }

    const toggleCalendar = e => {
      if (currentCalendar != null) {
        // When clicking on the label while the calendar is open we need to take into account
        // the possibility that the user clicked "the other date" in which case as per IG-695
        if (e.target.classList.contains('departure-title__return') && currentCalendar.instance.selectionMode === 'start') {
          currentCalendar.instance._toggleSelectionModeInRangeMode()
        } else if (e.target.classList.contains('departure-title__departure') && currentCalendar.instance.selectionMode === 'end') {
          currentCalendar.instance._toggleSelectionModeInRangeMode()
        } else {
          closeCalendar()
        }
      } else {
        let mode = 'start'
        if (e.target.classList.contains('departure-title__return')) {
          mode = 'end'
        }
        openCalendar(mode, form)
      }
    }

    title.addEventListener('click', toggleCalendar)

    form.dispatchEvent(new CustomEvent('grecos:calendar:initialized', { detail: { main } }))
  },

  createMainElementFor (form, dateField) {
    const main = document.createElement('div')
    main.classList.add('custom-select-date__container')

    main.dataset.updateQueryString = form.classList.contains('offer-search-advanced')
    main.dataset.initializeDatesToMinMax = form.classList.contains('form-filters-popup')
    dateField.appendChild(main)

    // Define read-only property so that it doesn't get overriden by mistake
    Object.defineProperty(main, 'data', {
      value: new DataAdapter(main, new DateDisplay(main)),
      writable: false,
    })

    return main
  },

  openDesktopCalendar (main, title, rangeField, mode, selectedDates, validDates, range, validRanges) {
    main.innerHTML = '<div class="calendar-app"></div>'

    // Make original placeholder invisible
    main.style.display = 'flex'

    const updateQueryString = main.dataset.updateQueryString === 'true'
    log.debug('Opening desktop calendar - updateQueryString:', updateQueryString)

    const el = main.querySelector('.calendar-app')

    const ranges = [ '1:5', '6:9', '10:13', '14:16' ]

    const fallbackVersion = false

    const calendar = new Calendar({
      el,
      propsData: { mode, selectedDates, validDates, range, ranges, validRanges, fallbackVersion },
    })

    calendar.$on('hoverFrom', (date, mode) => {
      if (mode === 'end') main.data.title.highlightReturnText()
      else main.data.title.highlightDepartureText()
      this.displayDateOfDeparture(main, date)
    })
    calendar.$on('hoverTo', (date, mode) => {
      if (mode === 'end') main.data.title.highlightReturnText()
      else main.data.title.highlightDepartureText()
      this.displayDateOfReturn(main, date)
    })
    calendar.$on('hoverDay', date => {
      main.data.title.highlightDepartureText()
      this.displayDateOfDeparture(main, date)
    })

    calendar.$on('selectFrom', date => {
      this.displayDateOfDeparture(main, date)
      this.selectDateOfDeparture(main, date, updateQueryString)
    })
    calendar.$on('selectTo', (date, { isAutomaticShift }) => {
      this.displayDateOfReturn(main, date)
      this.selectDateOfReturn(main, date, updateQueryString)
      if (updateQueryString) this.selectDateOfReturnInQueryString(date)
      if (!isAutomaticShift) {
        this.closeCalendar(main, calendar)
      }
    })
    calendar.$on('selectDate', date => {
      this.displayDateOfDeparture(main, date)
      this.selectDateOfDeparture(main, date, updateQueryString)
      if (main.data.mode === 'date') this.selectDateOfReturn(main, moment(date).add(10, 'days'))
      if (updateQueryString) this.selectDateOfDepartureinQueryString(date)
    })
    calendar.$on('selectRange', range => {
      this.closeCalendar(main, calendar)
      this.selectDurationInterval(main, range)
      if (updateQueryString) this.selectDurationIntervalInQueryString(range)
    })
    calendar.$on('selectionModeChanged', (mode, start, end, range) => {
      this.updateSelectionMode(main, title, mode, start, end, range, updateQueryString)
      if (updateQueryString) this.updateSelectionModeInQueryString(main.data.mode)
      this.updateSelectionModeInForm(main.closest('form'), mode)
    })
    calendar.$on('selectMonth', (start, end) => {
      this.updateSelectionMode(main, title, mode, start, end, range, updateQueryString)

      this.displayDateOfDeparture(main, start)
      this.selectDateOfDeparture(main, start)
      this.selectDateOfDepartureinQueryString(start)
      this.displayDateOfReturn(main, end)
      this.selectDateOfReturn(main, end, true)
      this.selectDateOfReturnInQueryString(end)
      if (updateQueryString) this.updateSelectionModeInQueryString(main.data.mode)
      this.updateSelectionModeInForm(main.closest('form'), mode)
      this.closeCalendar(main, calendar)
    })
    calendar.$on('close', () => {
      this.closeCalendar(main, calendar)
    })

    return calendar
  },

  openMobileCalendar (requiredMode, main, title, selectedDates, validDates) {
    const el = document.createElement('div')
    document.body.appendChild(el)
    el.zIndex = 2001

    const calendar = new MobileCalendar({
      el,
      propsData: { selectedDates, validDates },
    })

    calendar.$on('selectFrom', date => {
      this.displayDateOfDeparture(main, date)
      this.selectDateOfDeparture(main, date)
      this.selectDateOfDepartureinQueryString(date)
    })
    calendar.$on('selectTo', date => {
      this.displayDateOfReturn(main, date)
      this.selectDateOfReturn(main, date, true)
      this.selectDateOfReturnInQueryString(date)
    })
    calendar.$on('close', () => {
      this.closeCalendar(main, calendar)
    })

    return calendar
  },

  open (requiredMode, main, title, rangeField) {
    const openCalendar = ({ mode, selectedDates, validDates, range, validRanges }) => {
      if (window.innerWidth < BREAKPOINT.TABLET) {
        return this.openMobileCalendar(requiredMode, main, title, selectedDates, validDates)
      } else {
        return this.openDesktopCalendar(main, title, rangeField, mode, selectedDates, validDates, range, validRanges)
      }
    }

    main.data.validRanges = main.data.validRanges || []

    const mode = main.data.mode === 'date' ? 'date' : requiredMode
    const range = main.data.validRanges.find(range => range == '6:9') || main.data.validRanges[0] // 6:9 is default range
    const validRanges = main.data.validRanges.filter(range => range && range.trim() != '')
    const selectedDates = (main.data.mode == 'start' || main.data.mode == 'end') ? moment.range(main.data.selectedStart, main.data.selectedEnd) : moment.range(main.data.selectedStart, 0)
    const validDates = moment.range(main.data.validStart, main.data.validEnd)

    if (mode == 'end') main.data.title.highlightReturnText()
    else main.data.title.highlightDepartureText()

    const calendar = openCalendar({ mode, selectedDates, validDates, range, validRanges })
    calendar.onClose = () => {}

    return {
      close: () => this.closeCalendar(main, calendar),
      instance: calendar,
    }
  },

  displayTitle (title, text) {
    log.debug('displayTitle', text)
    const element = title.parentNode.querySelector('.section-label')
    if (element) {
      element.innerText = text
    } else {
      log.warn('Element with selector ".section-label" not found')
    }
  },

  displayDateOfDeparture (main, date) {
    log.debug('displayDateOfDeparture', date.format('YYYY-MM-DD'))
    main.data.displayStart = date
  },

  displayDateOfReturn (main, date) {
    log.debug('displayDateOfReturn')
    if (date.isSame(moment(0))) {
      main.data.displayEnd = date
    } else {
      main.data.displayEnd = date
    }
  },

  selectDateOfDeparture (main, date) {
    log.debug('selectDateOfDeparture', date.format('YYYY-MM-DD'))
    main.data.selectedStart = date
    triggerChangeEvent(main)
    triggerChangeEvent(main.parentNode.querySelector('.form-group:nth-of-type(1) .form-control'))
  },

  selectDateOfDepartureinQueryString (date) {
    setQueryStringParameter('DateOfDeparture', date.format('YYYYMMDD'))
  },

  selectDateOfReturn (main, date) {
    log.debug('selectDateOfReturn', date && date.format('YYYY-MM-DD'))
    main.data.selectedEnd = date
    triggerChangeEvent(main)
    triggerChangeEvent(main.parentNode.querySelector('.form-group:nth-of-type(2) .form-control'))
  },

  selectDateOfReturnInQueryString (date) {
    setQueryStringParameter('DateOfReturn', date.format('YYYYMMDD'))
  },

  removeDateOfReturnFromQueryString () {
    log.debug('removeDateOfReturnFromQueryString')
    removeQueryStringParameter('DateOfReturn')
  },
  
  removeDurationIntervalFromQueryString () {
    log.debug('removeDateOfReturnFromQueryString')
    removeQueryStringParameter('DurationInterval')
  },
  
  clearDurationInterval (main) {
    log.debug('clearDurationInterval')
    main.data.range = ''
  },
  
  selectDurationInterval (main, range) {
    log.debug('selectDurationInterval', range)
    main.data.range = range
    triggerChangeEvent(main)
    triggerChangeEvent(main.parentNode.querySelector('.form-group:nth-of-type(1) .form-control'))
  },

  selectDurationIntervalInQueryString (range) {
    setQueryStringParameter('DurationInterval', range)
  },
  
  closeCalendar (main, calendar) {
    log.debug('closeCalendar', calendar, calendar.$el, calendar.$el.parentNode)
    const parent = calendar.$el.parentNode
    calendar.$destroy()
    parent.removeChild(calendar.$el)
    main.style.display = 'none'
    main.data.title.clearHightligh()
    calendar.onClose()
  },
  
  updateSelectionMode (main, title, mode, start, end, range) {
    log.debug('updateSelectionMode', ...arguments)
    if (mode === 'date') {
      main.data.mode = 'date'
    } else {
      main.data.mode = mode == 'end' ? 'start' : mode
    }
    log.debug('selectionModeChanged', mode, start.format('YYYY-MM-DD'), end.format('YYYY-MM-DD'), range)
    this.displayDateOfDeparture(main, start)

    if (mode == 'start') {
      this.displayDateOfDeparture(main, start)
      this.displayTitle(title, 'Wylot od - do')
      this.toggleCalendarPosition(main)
      main.data.title.highlightDepartureText()
    } else if (mode == 'end') {
      this.displayTitle(title, 'Wylot od - do')
      this.displayDateOfReturn(main, end)
      this.toggleCalendarPosition(main)
      main.data.title.highlightReturnText()
    } else if (mode == 'date') {
      this.selectDateOfReturn(main, moment(start).add(10, 'days'))
      this.displayTitle(title, 'Wylot od - Liczba dni')
      this.selectDurationInterval(main, range)
      this.toggleCalendarPosition(main)
      main.data.title.highlightDepartureText()
    } else if (mode == 'month') {
      this.displayDateOfDeparture(main, start)
      this.selectDateOfReturn(main, end)
      this.displayTitle(title, 'Wylot od - do')
      this.toggleCalendarPosition(main)
      main.data.title.highlightDepartureText()
    } else {
      throw new Error(`Unknown mode '${mode}'`)
    }
  },

  updateSelectionModeInQueryString (mode) {
    if (mode == 'start') this.removeDurationIntervalFromQueryString()
    else if (mode == 'end') this.removeDurationIntervalFromQueryString()
    else if (mode == 'date') this.removeDateOfReturnFromQueryString()
  },
  // TODO: check if duration interval should be removed here
  updateSelectionModeInForm (form, mode) {
    if (mode == 'start') this.removeDurationIntervalFromForm(form)
    else if (mode == 'end') this.removeDurationIntervalFromForm(form)
  },

  removeDurationIntervalFromForm (form) {
    form.action = form.action.replace(/DurationInterval=.*?&/, '')
  },

  toggleCalendarPosition (main) {
    const body = document.querySelector('body')
    const target = main.querySelector('.calendar')
    const mode = main.data.mode
    const observerConfig = {
      childList: true,
    }

    const cb = mutationsList => {
      if (mutationsList.length) {
        if (mode == 'date') this.updateCalendarPosition(main, body)
        else this.resetCalendarPosition(main, body)
      }
    }
    
    const observer = new MutationObserver(cb)
    observer.observe(target, observerConfig)
  },

  ifOneOfPageClassExist (body) {
    const pageTypes = [ 'page--offer', 'page--listing' ]
    for (const type of pageTypes) {
      if (body.classList.contains(type)) return true
    }

    return false
  },

  /**
   * Because  we have already a few different offer searchers with different
   * set of filters at different positions we need to make sure that calendar
   * will be visible on tablet devices in two states: days selector and
   * days-range selector.
   *
   * @param {Element} main
   * @param {Element} body
   */
  updateCalendarPosition (main, body) {
    if (this.ifOneOfPageClassExist(body)) {
      main.classList.add('updatePosition')
    }
  },

  resetCalendarPosition (main, body) {
    if (this.ifOneOfPageClassExist(body)) {
      main.classList.remove('updatePosition')
    }
  },
}



// WEBPACK FOOTER //
// ./src/modules/calendar.js